import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { useResolveRequestHook } from '@/modules/shared/hooks/use-resolve-request.hook'
import { useMainStore } from '@/modules/shared/stores/main.store'

import { useDeleteScheduleMutation } from '../mutations/delete-schedule.mutation'
import { filterSchedulesService } from '../services/filter-schedules.service'
import {
  useScheduleFormStore,
  useScheduleFormStoreActions,
} from '../stores/schedule-form.store'
import type { ScheduleCard } from '../types/shedule-card.type'

export type SearchSchedulesPeriodData = {
  dtStart: Date
  dtEnd: Date
}

export function useScheduleList({ enabled = true } = {}) {
  const navigate = useNavigate()

  const { checkStatusOnRequest } = useResolveRequestHook()

  const {
    setOperationType,
    setSchedules,
    setSelectedSchedule,
    setIsOpenModalDetailsSchedule,
  } = useScheduleFormStoreActions()

  const { schedules, selectedSchedule } = useScheduleFormStore()
  const {
    actions: { setConfirmation, setCloseConfirmation },
  } = useMainStore()

  const form = useForm<SearchSchedulesPeriodData>({
    defaultValues: {
      dtStart: dayjs().subtract(10, 'minutes').toDate(),
      dtEnd: dayjs().add(1, 'day').startOf('day').toDate(),
    },
  })

  const { deleteSchedule } = useDeleteScheduleMutation({
    resolveRequest: checkStatusOnRequest,
    callback: () => {
      const updatedSchedules = schedules.filter(
        (schedule) =>
          schedule.idAgendamento !== selectedSchedule?.idAgendamento,
      )

      setSelectedSchedule(null)
      setSchedules(updatedSchedules)
    },
  })

  const handleGoBack = () => {
    navigate('/')
  }

  const handleStartNewSchedule = () => {
    setOperationType('add')
    navigate('/agendamento/adicionar')
  }

  const handleViewSchedule = (scheduleId: number) => {
    const schedule = schedules.find(
      (schedule) => schedule.idAgendamento === scheduleId,
    )

    if (!schedule) {
      return
    }

    setSelectedSchedule(schedule)
    setIsOpenModalDetailsSchedule(true)
  }

  const handleEditOrder = () => {
    setOperationType('edit')
    navigate(`/agendamento/editar/${selectedSchedule?.idAgendamento}`)
  }

  const handleOrderDelete = () => {
    setConfirmation({
      isOpen: true,
      message: 'Deseja excluir este agendamento?',
      onClose: () => {
        setCloseConfirmation()
      },
      onConfirm: () => {
        deleteSchedule({
          id: selectedSchedule?.idAgendamento,
        })
        setCloseConfirmation()
      },
      title: 'Confirmação de exclusão',
    })
  }

  const handleSelectActionToStart = (actionType: string) => {
    const actions = {
      delete: handleOrderDelete,
      edit: handleEditOrder,
      add: handleStartNewSchedule,
    }

    const action = actions[actionType]
    action()
  }

  const handleSelectScheduleToStartActions = (
    orderSelectedFn: ScheduleCard,
  ) => {
    if (selectedSchedule?.idAgendamento === orderSelectedFn.idAgendamento) {
      setSelectedSchedule(undefined)
    } else {
      setSelectedSchedule(orderSelectedFn)
    }
  }

  const handleFetchSchedules = async (data: SearchSchedulesPeriodData) => {
    try {
      const { body } = await filterSchedulesService({
        dtEnd: dayjs(data.dtEnd)
          .utc()
          .startOf('day')
          .format('DD-MM-YYYYTHH:mm:ss.SSS[Z]'),
        dtStart: dayjs(data.dtStart).utc().format('DD-MM-YYYYTHH:mm:ss.SSS[Z]'),
      })

      let schedules

      try {
        schedules = JSON.parse(body)
      } catch (error) {
        console.error('Erro ao fazer parse do JSON:', error)
        return
      }

      if (!Array.isArray(schedules) || schedules.length === 0) {
        return
      }

      setSchedules(schedules)
    } catch (error) {
      console.error('Erro ao buscar os horários:', error)
    }
  }

  useEffect(() => {
    if (enabled) {
      handleFetchSchedules(form.getValues())
    }
  }, [enabled])

  return {
    schedules,
    form,
    handleFetchSchedules,
    selectedSchedule,
    handleSelectScheduleToStartActions,
    handleSelectActionToStart,
    handleGoBack,
    handleViewSchedule,
  }
}
